import { memo, useState, useCallback } from "react";
import { Dialog, DialogTitle, DialogContent, Box, Typography, Chip, Button, Divider, Stack } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { useCommonStyles } from '../../styles/GenericStyle';
import { MtComStyleObj } from '../../styles/MtComStyleObj';
import { greyDark } from "../../colors";
import { useConfirm } from "../../contexts/confirmDialogContext";
import { useMoveMaterialsToSplot } from '../../api/vegApl';
import FullScreenActionLoader from '../common/FullScreenActionLoader';
import { useGlobalState } from "../../contexts/GlobalState";
import { useSnackbar } from '../../contexts/SnackbarContext';
import SPLotFormDialog from './CreateSPLotsDialog';
import { moveToSplot } from '../mintRegistration/constants';
import ScanInput from '../ScanInput';

export type MoveMaterialsDialogProps = {
    open: boolean;
    onClose: () => void;
    selectedMaterials: any[];
    currentSplotId: string;
    currentSplotStatus?: string;
    currentSplotCropId?: string;
    currentSplotCropName?: string;
    onSuccess?: () => void;
};

function MoveMaterialsDialog({
    open,
    onClose,
    selectedMaterials,
    currentSplotId,
    currentSplotStatus,
    currentSplotCropId,
    currentSplotCropName,
    onSuccess,
}: MoveMaterialsDialogProps) {
    const classes = useCommonStyles();
    const [splotId, setSplotId] = useState("");
    const [showCreateSPLotDialog, setShowCreateSPLotDialog] = useState(false);
    const { confirm } = useConfirm();
    const { state: globalState, state: { roleSelected = "" } } = useGlobalState();
    const { showSnackbar } = useSnackbar();

    const materialIds = selectedMaterials.map((m) => m.material_id ?? m.mat_id ?? "");
    const normalizedStatus = (currentSplotStatus ?? "").trim().toLowerCase();
    const canCreateSPLot = normalizedStatus === 'create' || normalizedStatus === 'created';

    const { mutate: moveMaterials, isLoading: isMoving } = useMoveMaterialsToSplot({
        mutation: {
            onSuccess: (data: any) => {
                showSnackbar({ message: data?.data?.message ?? 'Materials moved successfully', severity: 'success' });
                handleClose();
                onSuccess?.();
            },
            onError: (error: any) => {
                const detail = error?.response?.data?.detail;
                showSnackbar({ message: typeof detail === 'string' ? detail : 'Error moving materials', severity: 'error' });
            },
        },
    });

    const handleMoveToSPLot = async () => {
        if (!splotId.trim()) return;
        const ok: any = await confirm({
            title: "",
            description: <>Are you sure you want to move selected materials<br />to SP Lot <strong>{splotId.trim()}</strong></>,
            confirmText: "Confirm",
            cancelText: "Cancel",
        });
        if (ok) {
            moveMaterials({
                data: {
                    splot_id: currentSplotId,
                    new_splot_id: splotId.trim(),
                    material_ids: materialIds,
                    user_id: globalState?.user_id_db,
                    user_role: roleSelected ? [roleSelected] : [],
                },
            });
        }
    };

    const handleCreateSPLot = () => {
        setShowCreateSPLotDialog(true);
    };

    const handleSPLotCreated = useCallback(async (splotIds: string[]) => {
        const newSplotId = splotIds?.[0] ?? "";
        if (!newSplotId) return;
        setShowCreateSPLotDialog(false);
        const ok: any = await confirm({
            title: "",
            description: <>New SP Lot Created : <strong>{newSplotId}</strong><br />Are you sure you want to move selected materials<br />to new SP Lot ?</>,
            confirmText: "Confirm",
            cancelText: "Cancel",
        });
        if (ok) {
            moveMaterials({
                data: {
                    splot_id: currentSplotId,
                    new_splot_id: newSplotId,
                    material_ids: materialIds,
                    user_id: globalState?.user_id_db,
                    user_role: roleSelected ? [roleSelected] : [],
                },
            });
        }
    }, [currentSplotId, materialIds, globalState?.user_id_db, roleSelected, confirm, moveMaterials]);

    const handleClose = () => {
        setSplotId("");
        onClose();
    };

    return (
        <>
        <FullScreenActionLoader open={isMoving} />
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            sx={(theme) => ({ ...MtComStyleObj(theme).dialogBoxStyle })}
            slotProps={{ paper: { sx: { borderRadius: 3, p: 1 } } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
                <Typography className={classes.labelNoto20Bold600}>
                    Move Materials to SP Lot
                </Typography>
                <IconButton aria-label="close" onClick={handleClose}
                    sx={(theme) => ({ ...MtComStyleObj(theme).dialogCloseButton })}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 1 }}>
                {/* Selected Materials */}
                <Box sx={{ border: '1px solid #E0E0E0', borderRadius: 2, p: 2, mb: 3, backgroundColor: 'rgb(249, 250, 251)' }}>
                    <Typography className={classes.labelNoto14Bold400} sx={{ color: greyDark, mb: 1 }}>
                        Selected Materials ({selectedMaterials.length})
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                        {materialIds.map((id: string, index: number) => (
                            <Chip
                                key={`${id || 'material'}-${index}`}
                                label={id || 'N/A'}
                                variant="outlined"
                                size="small"
                                sx={{ borderRadius: 1 }}
                            />
                        ))}
                    </Stack>
                </Box>

                {/* Add to Existing SP Lot */}
                <Typography className={classes.labelNoto14Bold400} sx={{ color: greyDark, mb: 1 }}>
                    Add to Existing SP Lot
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                    <ScanInput
                        label=""
                        placeholder="Scan or Enter SP Lot ID"
                        defaultValue={splotId}
                        onScan={(value) => setSplotId(value)}
                        onChange={(value) => setSplotId(value)}
                        manualEdit
                    />
                    <Button
                        variant="contained"
                        disableRipple
                        className={classes.greenButton}
                        startIcon={<span dangerouslySetInnerHTML={{ __html: moveToSplot }} />}
                        onClick={handleMoveToSPLot}
                        disabled={!splotId.trim() || isMoving}
                        loading={isMoving}
                        sx={(theme) => ({
                            ...MtComStyleObj(theme).disabledGreenBtnStyle,
                            whiteSpace: 'nowrap',
                            minWidth: 160,
                            padding: "10px 16px",
                        })}
                    >
                        Move to SP Lot
                    </Button>
                </Stack>

                {/* Divider with "or" */}
                <Divider sx={{ my: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ }}>or</Typography>
                </Divider>

                {/* Create SP Lot */}
                <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<AddIcon />}
                    onClick={handleCreateSPLot}
                    disabled={!canCreateSPLot}
                    disableRipple
                    sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        height: 44,
                        borderColor: '#E0E0E0',
                        color: 'text.primary',
                        fontWeight: 500,
                        fontSize: 14,
                        '&:hover': {
                            borderColor: '#BDBDBD',
                            backgroundColor: '#FAFAFA',
                        },
                    }}
                >
                    Create SP Lot
                </Button>
            </DialogContent>
        </Dialog>
        <SPLotFormDialog
            open={showCreateSPLotDialog}
            setOpenSPLotDialog={setShowCreateSPLotDialog}
            setOpenAddMat={() => {}}
            setDrillDownCurrRow={() => {}}
            setRefreshSPLotGrid={() => {}}
            onCreated={handleSPLotCreated}
            preferredCropId={currentSplotCropId}
            preferredCropName={currentSplotCropName}
        />
        </>
    );
}

export default memo(MoveMaterialsDialog);
